import { useState, useRef, useEffect, useCallback } from "react";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { StreamingMessage } from "./StreamingMessage";
import {
  ToolCallChip,
  groupToolCalls,
  type ToolCallState,
} from "./ToolCallChip";
import { FlightResults } from "@/components/flights/FlightResults";
import { HotelResults } from "@/components/hotels/HotelResults";
import { resolveApiBaseUrl } from "@/lib/api/client";
import { useChatHistory } from "@/lib/api/hooks";
import type { Itinerary } from "@/lib/api/schemas";

type SearchResult =
  | { id: string; type: "flights"; data: any }
  | { id: string; type: "hotels"; data: any };

interface ItineraryCardSummary {
  dayCount: number;
  dayTitles: string[];
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  // Set when this assistant message was generated alongside an itinerary
  // update — we render a compact "itinerary card" in place of the prose,
  // with a toggle to reveal the full reasoning if the user wants it.
  itinerarySummary?: ItineraryCardSummary;
}

interface ChatPanelProps {
  tripId: string;
  onItineraryUpdate: (itinerary: Itinerary) => void;
  onItineraryUpdateReceived?: () => void;
  onRequestViewItinerary?: () => void;
  onAssistantMessage?: () => void;
  initialPrompt?: string;
}

export function ChatPanel({
  tripId,
  onItineraryUpdate,
  onItineraryUpdateReceived,
  onRequestViewItinerary,
  onAssistantMessage,
  initialPrompt,
}: ChatPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [streamingContent, setStreamingContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [toolCalls, setToolCalls] = useState<ToolCallState[]>([]);
  const [idleSeconds, setIdleSeconds] = useState(0);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoSentRef = useRef(false);

  // Load past conversation when opening an existing trip.
  const { data: history, isSuccess: historyLoaded } = useChatHistory(tripId);
  useEffect(() => {
    if (historyLoaded && history && messages.length === 0) {
      setMessages(
        history.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
        }))
      );
    }
  }, [historyLoaded, history, messages.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingContent, toolCalls]);

  const sendMessage = useCallback(
    async (text: string) => {
      const userMsg: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: text,
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsStreaming(true);
      setStreamingContent("");
      setToolCalls([]);
      setSearchResults([]);

      // Track itinerary state for this stream so we can attach a summary
      // to the finalized assistant message if an update happened.
      let latestItinerary: { days: Itinerary["days"] } | null = null;

      const token = localStorage.getItem("access_token");
      const baseUrl = resolveApiBaseUrl();

      try {
        const response = await fetch(`${baseUrl}/v1/trips/${tripId}/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message: text }),
        });

        if (!response.ok) {
          throw new Error(`Chat request failed: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No response body");

        const decoder = new TextDecoder();
        let buffer = "";
        let accumulated = "";

        // Accumulate days from itinerary_partial events so we can show
        // progressive itinerary builds without waiting for the final
        // itinerary_update event with the full payload.
        const partialDaysByNumber = new Map<number, any>();

        const flushPartial = () => {
          if (partialDaysByNumber.size === 0) return;
          const days = Array.from(partialDaysByNumber.values()).sort(
            (a, b) => (a.dayNumber ?? 0) - (b.dayNumber ?? 0)
          );
          latestItinerary = { days };
          onItineraryUpdate({ days });
          onItineraryUpdateReceived?.();
        };

        const dispatchEvent = (eventName: string, dataStr: string) => {
          if (!dataStr) return;
          let data: any;
          try {
            data = JSON.parse(dataStr);
          } catch {
            return;
          }

          switch (eventName) {
            case "text":
              if (typeof data.content === "string") {
                accumulated += data.content;
                setStreamingContent(accumulated);
              }
              break;
            case "tool_call":
              setToolCalls((prev) => [
                ...prev,
                { id: data.id, tool: data.tool, status: "running" },
              ]);
              // Reset partial day tracking when a new update_itinerary starts.
              if (data.tool === "update_itinerary") {
                partialDaysByNumber.clear();
              }
              break;
            case "tool_result":
              setToolCalls((prev) =>
                prev.map((t) =>
                  t.tool === data.tool && t.status === "running"
                    ? {
                        ...t,
                        status: data.result?.error ? "error" : "done",
                      }
                    : t
                )
              );
              // Capture search results so we can render flight/hotel cards
              // inline in the chat once the tool completes.
              if (data.tool === "search_flights" && data.result) {
                setSearchResults((prev) => [
                  ...prev,
                  { id: crypto.randomUUID(), type: "flights", data: data.result },
                ]);
              }
              if (data.tool === "search_hotels" && data.result) {
                setSearchResults((prev) => [
                  ...prev,
                  { id: crypto.randomUUID(), type: "hotels", data: data.result },
                ]);
              }
              break;
            case "itinerary_partial":
              // Day-by-day progressive itinerary streaming. Each event
              // contains a single day object that arrived before the full
              // tool input completed.
              if (data?.day && typeof data.day.dayNumber === "number") {
                partialDaysByNumber.set(data.day.dayNumber, data.day);
                flushPartial();
              }
              break;
            case "itinerary_update":
              if (data && Array.isArray(data.days)) {
                latestItinerary = data;
                onItineraryUpdate(data);
                onItineraryUpdateReceived?.();
                partialDaysByNumber.clear();
              }
              break;
            case "heartbeat":
              if (typeof data.idle_ms === "number") {
                setIdleSeconds(Math.round(data.idle_ms / 1000));
              }
              break;
            case "error":
              accumulated += `\n\n**Error:** ${data.message || "unknown error"}`;
              setStreamingContent(accumulated);
              break;
            case "done":
              break;
          }

          // Any non-heartbeat event resets the idle counter.
          if (eventName !== "heartbeat") {
            setIdleSeconds(0);
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          let eventEnd;
          while ((eventEnd = buffer.indexOf("\n\n")) !== -1) {
            const rawEvent = buffer.slice(0, eventEnd);
            buffer = buffer.slice(eventEnd + 2);

            let eventName = "message";
            let dataLines: string[] = [];
            for (const line of rawEvent.split("\n")) {
              if (line.startsWith("event: ")) {
                eventName = line.slice(7).trim();
              } else if (line.startsWith("data: ")) {
                dataLines.push(line.slice(6));
              }
            }
            dispatchEvent(eventName, dataLines.join("\n"));
          }
        }

        // Finalize assistant message, attaching an itinerary summary if
        // an update was emitted during this stream.
        if (accumulated) {
          const finalItin = latestItinerary as Itinerary | null;
          const itinerarySummary =
            finalItin && finalItin.days.length > 0
              ? {
                  dayCount: finalItin.days.length,
                  dayTitles: finalItin.days.map(
                    (d) => d.title || `Day ${d.dayNumber}`
                  ),
                }
              : undefined;
          const assistantMsg: Message = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: accumulated,
            itinerarySummary,
          };
          setMessages((prev) => [...prev, assistantMsg]);
          onAssistantMessage?.();
        }
      } catch (err) {
        console.error("Chat error:", err);
        const errorMsg: Message = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsStreaming(false);
        setStreamingContent("");
        setIdleSeconds(0);
        // Keep toolCalls visible after stream ends so the user can see what
        // was done. They're cleared on the next send.
      }
    },
    [tripId, onItineraryUpdate, onItineraryUpdateReceived, onAssistantMessage]
  );

  // Auto-fire the initial prompt exactly once per mount, but ONLY if the
  // trip has no prior conversation (otherwise we'd re-run the planning
  // every time the user reopens a saved trip).
  useEffect(() => {
    if (!initialPrompt || autoSentRef.current) return;
    if (!historyLoaded) return; // wait for history query to settle
    if (history && history.length > 0) {
      // Trip already has conversation — don't auto-send, just mark as done.
      autoSentRef.current = true;
      return;
    }
    autoSentRef.current = true;
    sendMessage(initialPrompt);
  }, [initialPrompt, historyLoaded, history, sendMessage]);

  // Are any tools currently running?
  const hasActiveTool = toolCalls.some((t) => t.status === "running");
  // Show the "thinking" pulse when the stream is active but we have no
  // visible activity yet (no text, no active tools).
  const showThinking =
    isStreaming && !streamingContent && !hasActiveTool && toolCalls.length === 0;

  return (
    <div className="flex flex-col h-full min-w-0">
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto min-w-0 px-4 pt-4 pb-6"
      >
        {messages.length === 0 && !isStreaming && (
          <div className="text-center text-ink-light mt-8">
            <p className="text-lg mb-2 text-ink">Start planning your trip</p>
            <p className="text-sm">
              Try: "Plan a 7-day trip to Italy focused on food and culture"
            </p>
          </div>
        )}
        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            role={msg.role}
            content={msg.content}
            itinerarySummary={msg.itinerarySummary}
            onViewItinerary={
              msg.itinerarySummary ? onRequestViewItinerary : undefined
            }
          />
        ))}

        {showThinking && <ThinkingIndicator />}

        {streamingContent && <StreamingMessage content={streamingContent} />}

        {toolCalls.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {groupToolCalls(toolCalls).map((g) => (
              <ToolCallChip key={g.tool} group={g} />
            ))}
          </div>
        )}

        {searchResults.length > 0 && (
          <div className="space-y-3 mb-3">
            {searchResults.map((r) =>
              r.type === "flights" ? (
                <FlightResults key={r.id} data={r.data} />
              ) : (
                <HotelResults key={r.id} data={r.data} />
              )
            )}
          </div>
        )}

        {isStreaming && (streamingContent || toolCalls.length > 0) && (
          <div className="flex items-center gap-2 text-xs text-ink-light ml-1">
            <span className="inline-block w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
            <span>
              {hasActiveTool ? "Working" : "Thinking"}
              {idleSeconds >= 3 ? ` · ${idleSeconds}s` : ""}
              {"..."}
            </span>
          </div>
        )}
      </div>
      <ChatInput onSend={sendMessage} disabled={isStreaming} />
    </div>
  );
}

function ThinkingIndicator() {
  return (
    <div className="flex justify-start mb-3">
      <div className="frost-card rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2 text-sm text-ink-light">
        <svg
          className="animate-spin w-4 h-4 text-accent"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span>Thinking...</span>
      </div>
    </div>
  );
}
